import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Circle,
  Autocomplete,
} from "@react-google-maps/api";

import {
  Row,
  Col,
  Card,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Table,
  Space,
  Grid,
} from "antd";

import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchDeliveryRoutePoints,
  saveRoutePoint,
  deleteDeliveryRoutePoint,
  fetchDeliveryAreas,
  saveDeliveryArea,
} from "../deliveryThunks";
import { GOOGLE_MAP_LIBRARIES } from "../../../constants/googleMaps";
import { selectCustomers } from "../../../store/commonSelectors/lookupsSelectors";
import { fetchCustomers } from "../../../store/commonThunks/lookups";

const containerStyle = {
  width: "100%",
  height: "calc(100vh - 180px)",
};

const libraries = GOOGLE_MAP_LIBRARIES;

const DeliveryRoutePoints = ({ center, zoom }) => {
  const dispatch = useDispatch();
  
  const { routePoints, routeAreas } = useSelector((state) => state.delivery);
  const customers = useSelector(selectCustomers);
  
  const defaultCenter = center || {
    lat: 25.2048,
    lng: 55.2708,
  };
  const mapApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: mapApiKey,
    libraries,
  });

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [map, setMap] = useState(null);
  const [newAreaName, setNewAreaName] = useState(null);
  const [open, setOpen] = useState(false);
  const [areaModalOpen, setAreaModalOpen] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [marker, setMarker] = useState(null);
  const [hoveredPointId, setHoveredPointId] = useState(null);
  const [locationSearch, setLocationSearch] = useState("");
  const [searchText, setSearchText] = useState("");

  const autocompleteRef = useRef(null);
  const [form] = Form.useForm();
  const radiusMeters = Form.useWatch("radius_meters", form);

  useEffect(() => {
    dispatch(fetchDeliveryRoutePoints());  
    dispatch(fetchDeliveryAreas());
    dispatch(fetchCustomers());
  }, [dispatch]);

  useEffect(() => {
    if (map && routePoints && routePoints.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      routePoints.forEach((point) => {
        bounds.extend({
          lat: Number(point.latitude),
          lng: Number(point.longitude),
        });
      });
      map.fitBounds(bounds);
    }
  }, [map, routePoints]);

  const routePointCustomers = useMemo(() => {
    return (customers || []).filter(
      (customer) => customer.acc_type_name === "Customer"
    );
  }, [customers]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      if (!marker) {
        return Modal.warning({
          title: "Location required",
        });
      }

      await dispatch(
        saveRoutePoint({
          ...values,
          latitude: marker.lat,
          longitude: marker.lng,
          address: values.address,
          id: selectedPoint?.id,
        }),
      ).unwrap();

      setOpen(false);
      form.resetFields();
      setMarker(null);
      setSelectedPoint(null);
      setLocationSearch("");
    } catch (err) {
      console.error("Save route point error:", err);

      Modal.error({
        title: "Unable to save route point",
        content:
          err?.message ||
          err?.error_description ||
          (typeof err === "string" ? err : "Something went wrong."),
      });
    }
  };

  const handleSaveArea = async () => {
    const res = await dispatch(
      saveDeliveryArea({
        name: newAreaName,
      })
    ).unwrap();

    dispatch(fetchDeliveryAreas());
    form.setFieldValue("area_id", res.id);
    setAreaModalOpen(false);
    setNewAreaName(null);
  };

  const openAddModal = () => {
    form.resetFields();
    form.setFieldsValue({
      related_type: "customer",
      radius_meters: 100,
    });
    setSelectedPoint(null);
    setMarker(null);
    setLocationSearch("");
    setOpen(true);
  };

  const openEditModal = (point) => {
    setSelectedPoint(point);
    form.setFieldsValue(point);
    setMarker({
      lat: Number(point.latitude),
      lng: Number(point.longitude),
    });
    setLocationSearch(point.address || "");
    setOpen(true);
  };

  const handleMapClick = (e) => {
    setMarker({
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    });
  };

  const onPlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();

    if (!place?.geometry) return;

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    const address = place.formatted_address;

    form.setFieldValue("address", address);

    setLocationSearch(address || "");

    setMarker({
      lat,
      lng,
    });

    map?.panTo({
      lat,
      lng,
    });

    map?.setZoom(15);
  };

  // Helper method to handle viewport boundaries and position centering shifts smoothly
  const handleRowHover = (record) => {
    setHoveredPointId(record.id);
    if (!map) return;

    const targetPos = {
      lat: Number(record.latitude),
      lng: Number(record.longitude),
    };

    const currentBounds = map.getBounds();
    // Verify visibility boundary status before shifting camera positions
    if (currentBounds && !currentBounds.contains(targetPos)) {
      map.panTo(targetPos);
    }
  };

  // search bar
  const filteredRoutePoints = useMemo(() => {
  if (!searchText) return routePoints;

  const search = searchText.toLowerCase();

  return routePoints.filter((point) =>
    [
      point.name,
      point.related_type,
      point.area_name,
      point.address,
    ]
      .filter(Boolean)
      .some((value) =>
        String(value).toLowerCase().includes(search)
      )
  );
}, [routePoints, searchText]);

  // related type watch
  const relatedType = Form.useWatch("related_type", form);

  const hasCustomerRoutePoint = useMemo(() => {
    return routePoints.some(
      (point) => point.related_type === "customer"
    );
  }, [routePoints]);

  const columns = [
    {
      title: "#",
      key: "serialNumber",
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Name",
      dataIndex: "name",
    },
    {
      title: "Type",
      dataIndex: "related_type",
    },
    ...( hasCustomerRoutePoint ? [{
      title: "Customer",
      dataIndex: "customer_name",
      render: (value, record) =>
        record?.related_type === "customer"
          ? value || "-"
          : "-",
    }]: [] ),
    {
      title: "Radius",
      dataIndex: "radius_meters",
      render: (v) => `${v}m`,
    },
    {
      title: "Area",
      dataIndex: "area_name",
    },
    {
      title: "Action",
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => openEditModal(record)} />          
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => dispatch(deleteDeliveryRoutePoint({ id: record.id }))}
          />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "8px" }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={8}>
          <Card
            title={`Route Points (${routePoints.length})`}
            extra={
              <Button
                type="primary"
                size={screens.sm ? "middle" : "small"}
                onClick={openAddModal}
              >
                Add Route Point
              </Button>
            }
            styles={{
              header: {
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                rowGap: 8,
              },
            }}
          >
            <Input.Search
              placeholder="Search route points..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ marginBottom: 12 }}
            />
            <Table
              dataSource={filteredRoutePoints}
              columns={columns}
              rowKey="id"
              pagination={false}
              scroll={{ x: "max-content" }}
              size="small"
              onRow={(record) => ({
                onMouseEnter: () => handleRowHover(record),
                onMouseLeave: () => setHoveredPointId(null),
              })}
            />
          </Card>
        </Col>

        <Col xs={24} lg={16}>
          {isLoaded && (
            <GoogleMap
              mapContainerStyle={{
                width: "100%",
                height: isMobile ? "50vh" : "100vh",
                minHeight: "350px",
                maxHeight: "calc(100vh - 180px)",
              }}
              center={defaultCenter}
              zoom={zoom || 11}
              onLoad={(m) => setMap(m)}
            >
              {routePoints.map((point, index) => {
                const isHovered = point.id === hoveredPointId;
                return (
                  <React.Fragment key={point.id}>
                    <Marker
                      position={{
                        lat: Number(point.latitude),
                        lng: Number(point.longitude),
                      }}
                      title={`${index + 1}. ${point.name}`}
                      label={{
                        text: String(index + 1),
                        color: "#ffffff",
                        fontWeight: "bold",
                        fontSize: "12px",
                      }}
                      animation={
                        isHovered && window.google
                          ? window.google.maps.Animation.BOUNCE
                          : null
                      }
                    />
                    <Circle
                      center={{
                        lat: Number(point.latitude),
                        lng: Number(point.longitude),
                      }}
                      radius={Number(point.radius_meters)}
                      options={{
                        fillColor: isHovered ? "#ff4d4f" : "#1890ff",
                        fillOpacity: isHovered ? 0.3 : 0.15,
                        strokeColor: isHovered ? "#ff4d4f" : "#1890ff",
                        strokeWeight: isHovered ? 2 : 1,
                      }}
                    />
                  </React.Fragment>
                );
              })}
            </GoogleMap>
          )}
        </Col>
      </Row>

      <Modal
        open={open}
        title={selectedPoint ? "Edit Route Point" : "Add Route Point"}
        onOk={handleSave}
        onCancel={() => setOpen(false)}
        width={900}
        style={{ top: 20 }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={10}>
            <Form layout="vertical" form={form}>
              <Form.Item
                label="Route Point Name"
                name="name"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>

              <Form.Item label="Related Type" name="related_type">
                <Select
                  options={[
                    {
                      label: "Customer",
                      value: "customer",
                    },
                    {
                      label: "Branch",
                      value: "branch",
                    },
                    {
                      label: "Custom",
                      value: "custom",
                    },
                  ]}
                  onChange={() => {
                    form.setFieldValue("related_id", null);
                  }}
                />
              </Form.Item>

              {relatedType === "customer" && (
                <Form.Item
                  label="Customer"
                  name="related_id"
                  rules={[
                    {
                      required: true,
                      message:
                        "Customer is required. Otherwise, select a different related type.",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    allowClear
                    placeholder="Select Customer"
                    optionFilterProp="label"
                    options={routePointCustomers.map((customer) => ({
                      label: customer.acc_code
                        ? `${customer.acc_name} (${customer.acc_code})`
                        : customer.acc_name,
                      value: customer.acc_id,
                    }))}
                    onChange={(customerId) => {
                      if (!customerId) {
                        form.setFieldValue("address", "");

                        setLocationSearch("");
                        setMarker(null);

                        return;
                      }

                      const customer = routePointCustomers.find(
                        (item) => item.acc_id === customerId,
                      );

                      if (!customer) return;

                      form.setFieldValue("address", customer.address || "");

                      setLocationSearch(customer.address || "");
                    }}
                  />
                </Form.Item>
              )}

              <Form.Item
                label="Area"
                name="area_id"
                rules={[{ required: true, message: "Area required" }]}
              >
                <Select
                  showSearch
                  options={routeAreas.map((area) => ({
                    label: area.name,
                    value: area.id,
                  }))}
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <Button
                        type="link"
                        block
                        onClick={() => setAreaModalOpen(true)}
                      >
                        + Add New Area
                      </Button>
                    </>
                  )}
                />
              </Form.Item>

              <Form.Item
                label="Radius (meters)"
                name="radius_meters"
                initialValue={100}
              >
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item label="Address" name="address">
                <Input.TextArea rows={3} />
              </Form.Item>
            </Form>
          </Col>

          <Col xs={24} md={14}>
            <Autocomplete
              onLoad={(a) => (autocompleteRef.current = a)}
              onPlaceChanged={onPlaceChanged}
            >
              <Input
                placeholder="Search location"
                value={locationSearch}
                onChange={(e) => setLocationSearch(e.target.value)}
                style={{ marginBottom: 10 }}
              />
            </Autocomplete>

            {isLoaded && (
              <GoogleMap
                mapContainerStyle={{
                  width: "100%",
                  height: "350px",
                }}
                center={marker || defaultCenter}
                zoom={marker ? 15 : 10}
                onClick={handleMapClick}
              >
                {marker && (
                  <>
                    <Marker
                      position={marker}
                      draggable
                      onDragEnd={(e) =>
                        setMarker({
                          lat: e.latLng.lat(),
                          lng: e.latLng.lng(),
                        })
                      }
                    />
                    <Circle
                      center={marker}
                      radius={Number(radiusMeters || 100)}
                      options={{
                        fillColor: "#ff4d4f",
                        fillOpacity: 0.15,
                        strokeColor: "#ff4d4f",
                        strokeWeight: 1,
                      }}
                    />
                  </>
                )}
              </GoogleMap>
            )}
          </Col>
        </Row>
      </Modal>

      <Modal
        open={areaModalOpen}
        title="Add Area"
        onOk={handleSaveArea}
        onCancel={() => setAreaModalOpen(false)}
      >
        <Input
          value={newAreaName}
          onChange={(e) => setNewAreaName(e.target.value)}
          placeholder="Area Name"
        />
      </Modal>
    </div>
  );
};

export default DeliveryRoutePoints;